import DataQuery from "data/DataQuery";
import { ContractualAccounts_ContactPersons, Customer_FuelingContracts } from "data/endpoints";
import { useQuery } from "data/hooks/useQuery";
import ContractSimplifiedDTO from "features/contracts/dtos/ContractSimplifiedDTO";
import DataFilterModal from "features/shared/components/dataFilterModal/DataFilterModal";
import DataGridHeader from "features/shared/components/dataGrid/DataGridHeader";
import DataGridRow from "features/shared/components/dataGrid/DataGridRow";
import { useStateDataGrid } from "features/shared/components/dataGrid/hooks/useStateDataGrid";
import NoContentWrapper from "features/shared/components/noContentWrapper/NoContentWrapper";
import Pagination from "features/shared/components/pagination/Pagination";
import PagedDataResultDTO from "features/shared/dtos/PagedDataResultDTO";
import { loadDataFilterConfig } from "features/shared/helpers/modules";
import { getRouteParams } from "features/shared/helpers/routeParams";
import CustomerState from "infrastructure/store/customer/CustomerState";
import IAppState from "infrastructure/store/IAppState";
import React from "react";
import { useSelector } from "react-redux";
import { ContactPersonType } from "features/contractualAccounts/enums/contactPersonType";
import { getQueryParams } from "features/shared/helpers/queryParams";
import ContactPersonDTO from "features/contractualAccounts/dtos/ContactPersonDTO";
import ContactPersonInline from 'features/contractualAccounts/components/ContactPersonInline';
import FuelingContractSimplifiedHeader from "features/contracts/components/FuelingContractSimplifiedHeader";
import FuelingContractSimplified from "features/contracts/components/FuelingContractSimplified";

const fuelingContractsDataFilterJson = loadDataFilterConfig("fuelingContractsDataFilter");

const pageSize = 10;

type Props = {
  contractualAccountId: string;
}

const ContractualAccountDetailFuelingContractsSection = ({ contractualAccountId }: Props) => {
  //#region store
  const customer = useSelector<IAppState, CustomerState>((store) => store.customer).customer;
  //#endregion store

  //#region data grid hook
  const {
    dataGrid,
    descriptors,
    setDescriptors,
    showFilter,
    setShowFilter,
    paginationPageChangeHandler,
    filterApplyHandler,
    filterResetHandler,
    isApplyFilter,
    conditionalRefresh
  } = useStateDataGrid(pageSize, fuelingContractsDataFilterJson);
  //#endregion data grid hook

  //#region fetch data
  // contracts
  const getContractsQueryParams = (): URLSearchParams => {
    let queryParams = new URLSearchParams(dataGrid.computedUrlSearchParams);
    queryParams.append("ContractualAccountId", contractualAccountId);

    return queryParams;
  };

  const query: DataQuery = {
    url: Customer_FuelingContracts,
    params: getContractsQueryParams(),
    routeParams: getRouteParams("customerId", customer.customerId!)
  };

  const { data: fuelingContracts, loadStatus: fuelingContractsStatus } = useQuery<
    PagedDataResultDTO<ContractSimplifiedDTO>
  >(query, { count: 0, result: [] }, [dataGrid.computedUrlSearchParams, customer.customerId], { useTracker: true }, conditionalRefresh);

  // contact person
  const contactPersonsQuery: DataQuery = {
    url: ContractualAccounts_ContactPersons,
    routeParams: getRouteParams("contractualAccountId", contractualAccountId),
    params: getQueryParams("ContactPersonType", [ContactPersonType.ContactPerson])
  };
  const { data: contactPersons, loadStatus: contactPersonsLoadStatus } = useQuery<ContactPersonDTO[]>(contactPersonsQuery, [], [contractualAccountId]);
  //#endregion fetch data

  //#region sub-renders
  const renderContracts = () => {
    return fuelingContracts.result.map((item) =>
      <DataGridRow key={item.contractId}>
        <FuelingContractSimplified contract={item} />
      </DataGridRow>
    );  
  } 

  const renderContactPerson = () => {
    let contactPerson = contactPersons.find(x => x.contactPersonType === "ContactPerson");
    if (contactPerson) {
      return <ContactPersonInline person={contactPerson} />;
    }
    return <></>;
  }
  //#endregion sub-renders

  //#region render
  return (
    <NoContentWrapper
      data={fuelingContracts}
      loadStatus={fuelingContractsStatus}
      noContentAsAlert
      isActiveFilter={isApplyFilter}
    >
      <DataGridHeader 
        count={fuelingContracts.count}
        setShowFilterModal={setShowFilter}
        filterActive={isApplyFilter()}
        renderAnotherInfo={renderContactPerson}
        // actionBtns={() => <InvoiceSimulationButton />}
      />
      <FuelingContractSimplifiedHeader />
      {renderContracts()}
      <div className="d-flex justify-content-center mt-4">
        <Pagination
          urlParams={new URLSearchParams(dataGrid.computedUrlSearchParams)}
          count={fuelingContracts.count!}
          pageSize={pageSize}
          onPageChange={paginationPageChangeHandler}
        />
      </div>

      <DataFilterModal
        descriptors={descriptors}
        setDescriptors={setDescriptors}
        applyButtonOnClick={filterApplyHandler}
        resetButtonOnClick={filterResetHandler}
        show={showFilter}
        setShow={setShowFilter}
      />
    </NoContentWrapper>
  );
  //#endregion render
}

export default ContractualAccountDetailFuelingContractsSection;